import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Terminal, Plus, RefreshCw, Play, Trash2, Edit2, Clock, Tag as TagIcon,
  Layers, KeyRound, Lock, X, ChevronRight, CheckCircle, XCircle, Loader,
  Library, Sparkles, Copy, UploadCloud, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Fetch helper (JWT desde localStorage, igual que el resto de la app) ──────
async function af(path, opts = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/automation${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
}

const LANG_STYLE = {
  powershell: { label: 'PowerShell', bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  bash:       { label: 'Bash',       bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  python:     { label: 'Python',     bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)' },
  cmd:        { label: 'CMD',        bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
};
const TAG_COLORS = {
  slate:  'var(--ink-2)', blue: 'var(--accent)', green: 'var(--green)', amber: 'var(--amber)',
  red:    'var(--red)', violet: 'var(--ink-2)', teal: '#0F766E', pink: '#BE185D',
};

function Badge({ style, children }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 600, lineHeight: 1,
      padding: '3px 8px', borderRadius: 4,
      background: style.bg, color: style.color, border: `1px solid ${style.border}`,
    }}>{children}</span>
  );
}

function Modal({ title, onClose, children, width = 640 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: width, padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--bg)' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-2)' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 };
const input = { width: '100%', height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--surface)', color: 'var(--ink-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', cursor: 'pointer', color: 'var(--ink-2)' };

const TABS = [
  { key: 'consultas', label: 'Consultas', icon: Search },
  { key: 'scripts', label: 'Scripts', icon: Terminal },
  { key: 'library', label: 'Biblioteca', icon: Library },
  { key: 'tasks',   label: 'Tareas',  icon: Clock },
  { key: 'runs',    label: 'Ejecuciones', icon: Play },
  { key: 'vars',    label: 'Variables globales', icon: KeyRound },
  { key: 'tags',    label: 'Tags & Scopes', icon: TagIcon },
];

export default function Automation() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || (user?.extra_roles || []).includes('admin');
  const [tab, setTab] = useState('scripts');
  const [err, setErr] = useState('');

  return (
    <div style={{ maxWidth: 1150 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Terminal size={22} style={{ opacity: 0.7 }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Automatización</h1>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-2)' }}>Scripts, variables, tags y ejecución remota como SYSTEM / root vía agente</p>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--amber-dim)', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Lock size={14} /> Solo lectura — la creación y ejecución de scripts requiere rol de administrador.
        </div>
      )}
      {err && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--red-dim)', border: '1px solid #FECDD3', borderRadius: 8, fontSize: 12, color: 'var(--red)' }}>{err}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setErr(''); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: 'none', background: 'none', fontSize: 13, fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? 'var(--ink)' : 'var(--ink-2)', borderBottom: tab === t.key ? '2px solid var(--ink)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer' }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'consultas' && <ConsultasTab isAdmin={isAdmin} onErr={setErr} />}
      {tab === 'scripts' && <ScriptsTab isAdmin={isAdmin} onErr={setErr} onGoLibrary={() => setTab('library')} />}
      {tab === 'library' && <LibraryTab isAdmin={isAdmin} onErr={setErr} onUsed={() => setTab('scripts')} />}
      {tab === 'tasks'   && <TasksTab isAdmin={isAdmin} onErr={setErr} />}
      {tab === 'runs'    && <RunsTab onErr={setErr} />}
      {tab === 'vars'    && <VarsTab isAdmin={isAdmin} onErr={setErr} />}
      {tab === 'tags'    && <TagsTab isAdmin={isAdmin} onErr={setErr} />}
    </div>
  );
}

// ════════════════════════════ SCRIPTS ════════════════════════════
function ScriptsTab({ isAdmin, onErr, onGoLibrary }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // script obj or {} for new
  const [running, setRunning] = useState(null);  // script to run
  const [publishing, setPublishing] = useState(null); // script to publish

  const load = useCallback(() => {
    setLoading(true);
    af('/scripts').then(setScripts).catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (!window.confirm(`¿Eliminar el script "${s.name}"?`)) return;
    try { await af(`/scripts/${s.id}`, { method: 'DELETE' }); load(); }
    catch (e) { onErr(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {isAdmin && <button style={btnPrimary} onClick={() => setEditing({})}><Plus size={15} /> Nuevo script</button>}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <Empty spin /> : scripts.length === 0 ? <Empty icon={Terminal} text="No hay scripts todavía" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
              {['Nombre', 'Lenguaje', 'SO', 'Carpeta', 'Actualizado', ''].map(h =>
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 12 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {scripts.map((s, i) => {
                const ls = LANG_STYLE[s.language] || LANG_STYLE.bash;
                return (
                  <tr key={s.id} style={{ borderBottom: i < scripts.length - 1 ? '1px solid var(--bg)' : 'none' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{s.name}</div>
                      {s.description && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{s.description}</div>}
                    </td>
                    <td style={{ padding: '12px 14px' }}><Badge style={ls}>{ls.label}</Badge></td>
                    <td style={{ padding: '12px 14px', color: 'var(--ink-2)', textTransform: 'capitalize' }}>{s.os_target || 'any'}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{s.folder || '—'}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmtDate(s.updated_at)}</td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {isAdmin && <>
                        <button title="Ejecutar" style={{ ...iconBtn, color: 'var(--green)', marginRight: 6 }} onClick={() => setRunning(s)}><Play size={14} /></button>
                        <button title="Editar" style={{ ...iconBtn, marginRight: 6 }} onClick={async () => setEditing(await af(`/scripts/${s.id}`))}><Edit2 size={14} /></button>
                        <button title={s.kb_article_id ? 'Ya publicado a la biblioteca' : 'Publicar a la biblioteca'} disabled={!!s.kb_article_id}
                          style={{ ...iconBtn, marginRight: 6, color: s.kb_article_id ? 'var(--green)' : 'var(--ink-2)', opacity: s.kb_article_id ? 0.6 : 1 }}
                          onClick={() => setPublishing(s)}><UploadCloud size={14} /></button>
                        <button title="Eliminar" style={{ ...iconBtn, color: 'var(--red)' }} onClick={() => del(s)}><Trash2 size={14} /></button>
                      </>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {editing && <ScriptEditor script={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onErr={onErr} />}
      {running && <RunModal script={running} onClose={() => setRunning(null)} onErr={onErr} />}
      {publishing && <PublishModal script={publishing} onClose={() => setPublishing(null)}
        onDone={() => { setPublishing(null); load(); }} onErr={onErr} onGoLibrary={onGoLibrary} />}
    </div>
  );
}

function PublishModal({ script, onClose, onDone, onErr, onGoLibrary }) {
  const [cat, setCat] = useState(script.library_category || 'General');
  const [saving, setSaving] = useState(false);
  const publish = async () => {
    setSaving(true);
    try {
      await af(`/scripts/${script.id}/publish`, { method: 'POST', body: JSON.stringify({ library_category: cat }) });
      onDone();
    } catch (e) { onErr(e.message); setSaving(false); }
  };
  return (
    <Modal title={`Publicar "${script.name}" a la biblioteca`} onClose={onClose} width={460}>
      <p style={{ marginTop: 0, fontSize: 13, color: 'var(--ink-2)' }}>
        Se crea una copia reutilizable en la Biblioteca y una entrada buscable en la Base de Conocimiento (categoría <b>Scripts</b>).
      </p>
      <div style={{ marginBottom: 16 }}>
        <label style={label}>Categoría en la biblioteca</label>
        <input style={input} value={cat} onChange={e => setCat(e.target.value)} placeholder="Mantenimiento" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={publish}><UploadCloud size={14} /> {saving ? 'Publicando…' : 'Publicar'}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════ BIBLIOTECA ════════════════════════════
function LibraryTab({ isAdmin, onErr, onUsed }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    af('/library').then(setItems).catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); }, [load]);

  const use = async (t) => {
    try { await af(`/library/${t.id}/use`, { method: 'POST' }); onErr(''); onUsed(); }
    catch (e) { onErr(e.message); }
  };
  const del = async (t) => {
    if (!window.confirm(`¿Quitar "${t.name}" de la biblioteca? También se borra su entrada en la KB.`)) return;
    try { await af(`/scripts/${t.id}`, { method: 'DELETE' }); load(); } catch (e) { onErr(e.message); }
  };

  if (loading) return <div className="card"><Empty spin /></div>;
  if (items.length === 0) return <div className="card"><Empty icon={Library} text="La biblioteca está vacía" /></div>;

  // agrupar por categoría
  const groups = {};
  for (const it of items) { const c = it.library_category || 'General'; (groups[c] = groups[c] || []).push(it); }

  return (
    <div>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--ink-2)' }}>
        Plantillas reutilizables. <b>Usar</b> crea una copia editable en Scripts lista para ejecutar. Cada plantilla también queda buscable en la Base de Conocimiento.
      </p>
      {Object.keys(groups).sort().map(cat => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{cat}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {groups[cat].map(t => {
              const ls = LANG_STYLE[t.language] || LANG_STYLE.bash;
              return (
                <div key={t.id} className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</span>
                    <Badge style={ls}>{ls.label}</Badge>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)', flex: 1, minHeight: 34 }}>{t.description || '—'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'capitalize' }}>{t.os_target || 'any'}</span>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...btnGhost, padding: '6px 12px' }} onClick={() => use(t)}><Copy size={13} /> Usar</button>
                        <button title="Quitar de la biblioteca" style={{ ...iconBtn, color: 'var(--red)' }} onClick={() => del(t)}><Trash2 size={13} /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ScriptEditor({ script, onClose, onSaved, onErr }) {
  const isNew = !script.id;
  const [f, setF] = useState({
    name: script.name || '', description: script.description || '',
    language: script.language || 'powershell', os_target: script.os_target || 'any',
    folder: script.folder || '', timeout_sec: script.timeout_sec || 300, content: script.content || '',
  });
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const generate = async () => {
    if (!aiPrompt.trim()) { onErr('Describí qué debe hacer el script.'); return; }
    setAiLoading(true);
    try {
      const r = await af('/scripts/generate', { method: 'POST', body: JSON.stringify({ prompt: aiPrompt, language: f.language, os_target: f.os_target }) });
      set('content', r.content);
      onErr('');
    } catch (e) { onErr(e.message); } finally { setAiLoading(false); }
  };

  const save = async () => {
    if (!f.name || !f.content) { onErr('Nombre y contenido son requeridos.'); return; }
    setSaving(true);
    try {
      if (isNew) await af('/scripts', { method: 'POST', body: JSON.stringify(f) });
      else await af(`/scripts/${script.id}`, { method: 'PUT', body: JSON.stringify(f) });
      onSaved();
    } catch (e) { onErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Nuevo script' : 'Editar script'} onClose={onClose} width={760}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: '1 / 3' }}>
          <label style={label}>Nombre</label>
          <input style={input} value={f.name} onChange={e => set('name', e.target.value)} placeholder="Detectar notebook y taggear" />
        </div>
        <div>
          <label style={label}>Lenguaje</label>
          <select style={input} value={f.language} onChange={e => set('language', e.target.value)}>
            <option value="powershell">PowerShell</option>
            <option value="cmd">CMD (batch)</option>
            <option value="bash">Bash</option>
            <option value="python">Python</option>
          </select>
        </div>
        <div>
          <label style={label}>Sistema operativo</label>
          <select style={input} value={f.os_target} onChange={e => set('os_target', e.target.value)}>
            <option value="any">Cualquiera</option>
            <option value="windows">Windows</option>
            <option value="linux">Linux</option>
          </select>
        </div>
        <div>
          <label style={label}>Carpeta (opcional)</label>
          <input style={input} value={f.folder} onChange={e => set('folder', e.target.value)} placeholder="Mantenimiento" />
        </div>
        <div>
          <label style={label}>Timeout (segundos)</label>
          <input style={input} type="number" value={f.timeout_sec} onChange={e => set('timeout_sec', parseInt(e.target.value) || 300)} />
        </div>
        <div style={{ gridColumn: '1 / 3' }}>
          <label style={label}>Descripción (opcional)</label>
          <input style={input} value={f.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div style={{ gridColumn: '1 / 3', padding: 12, background: 'var(--bg-subtle)', border: '1px solid #DDD6FE', borderRadius: 8 }}>
          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-2)' }}><Sparkles size={14} /> Generar con IA</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, background: 'var(--surface)' }} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') generate(); }}
              placeholder="Ej: liberar espacio borrando logs de más de 30 días y reportar cuánto se liberó" />
            <button style={{ ...btnPrimary, background: 'var(--ink-2)', whiteSpace: 'nowrap' }} disabled={aiLoading} onClick={generate}>
              {aiLoading ? <Loader size={14} style={{ animation: 'spin .8s linear infinite' }} /> : <Sparkles size={14} />} {aiLoading ? 'Generando…' : 'Generar'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 6 }}>Genera un borrador en {f.language} para {f.os_target === 'any' ? 'cualquier SO' : f.os_target}. Revisalo antes de ejecutar.</div>
        </div>
        <div style={{ gridColumn: '1 / 3' }}>
          <label style={label}>Contenido del script</label>
          <textarea value={f.content} onChange={e => set('content', e.target.value)} spellCheck={false}
            style={{ width: '100%', minHeight: 240, padding: 12, border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', boxSizing: 'border-box', lineHeight: 1.5 }}
            placeholder={'# Usá {{VARIABLE}} para insertar variables globales.\n# Emití ##ITIL-SET-TAGS: Notebook para taggear el equipo.'} />
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
            Variables globales: <code>{'{{MI_VARIABLE}}'}</code> · Auto-tag desde el output: <code>##ITIL-SET-TAGS: Notebook,SSD</code> / <code>##ITIL-REMOVE-TAGS: Desktop</code>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
      </div>
    </Modal>
  );
}

// ── Selector de destino reutilizable (run modal + tareas) ────────────────────
function TargetPicker({ target, setTarget, tags, scopes, assets }) {
  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Destino</label>
        <select style={input} value={target.type} onChange={e => setTarget({ type: e.target.value, id: '' })}>
          <option value="scope">Scope</option>
          <option value="tag">Tag</option>
          <option value="asset">Un equipo</option>
          <option value="all">Todos los equipos con agente</option>
        </select>
      </div>
      {target.type === 'scope' && (
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Scope</label>
          <select style={input} value={target.id} onChange={e => setTarget({ ...target, id: e.target.value })}>
            <option value="">Elegir scope…</option>
            {scopes.map(s => <option key={s.id} value={s.id}>{s.name} ({s.asset_count} equipos)</option>)}
          </select>
        </div>
      )}
      {target.type === 'tag' && (
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Tag</label>
          <select style={input} value={target.id} onChange={e => setTarget({ ...target, id: e.target.value })}>
            <option value="">Elegir tag…</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name} ({t.asset_count})</option>)}
          </select>
        </div>
      )}
      {target.type === 'asset' && (
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Equipo</label>
          <select style={input} value={target.id} onChange={e => setTarget({ ...target, id: e.target.value })}>
            <option value="">Elegir equipo…</option>
            {assets.map(a => <option key={a.id} value={a.id}>{a.hostname} {a.os_name ? `— ${a.os_name}` : ''}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

function useTargetData(onErr) {
  const [tags, setTags] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [assets, setAssets] = useState([]);
  useEffect(() => {
    af('/tags').then(setTags).catch(() => {});
    af('/scopes').then(setScopes).catch(() => {});
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/assets?limit=1000`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAssets(Array.isArray(d) ? d : (d.assets || d.rows || []))).catch(() => {});
  }, [onErr]);
  return { tags, scopes, assets };
}

function RunModal({ script, onClose, onErr }) {
  const { tags, scopes, assets } = useTargetData(onErr);
  const [target, setTarget] = useState({ type: 'scope', id: '' });
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (target.type !== 'all' && !target.id) { setPreview(null); return; }
    af('/preview-targets', { method: 'POST', body: JSON.stringify({ target }) })
      .then(d => setPreview(d.count)).catch(() => setPreview(null));
  }, [target]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await af('/run', { method: 'POST', body: JSON.stringify({ script_id: script.id, target }) });
      setDone(r);
    } catch (e) { onErr(e.message); setRunning(false); }
  };

  return (
    <Modal title={`Ejecutar: ${script.name}`} onClose={onClose} width={520}>
      {done ? (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <CheckCircle size={40} style={{ color: 'var(--green)', marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Ejecución encolada</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>Se envió a {done.total_targets} equipo(s). El resultado aparece en la pestaña Ejecuciones cuando el agente responda.</div>
          <button style={btnPrimary} onClick={onClose}>Listo</button>
        </div>
      ) : (
        <>
          <TargetPicker target={target} setTarget={setTarget} tags={tags} scopes={scopes} assets={assets} />
          <div style={{ padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 7, fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>
            {preview === null ? 'Elegí un destino para ver cuántos equipos alcanza.' :
              <><b>{preview}</b> equipo(s) coinciden con este destino.</>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={btnGhost} onClick={onClose}>Cancelar</button>
            <button style={{ ...btnPrimary, background: 'var(--green)' }} disabled={running || !preview}
              onClick={run}><Play size={14} /> {running ? 'Ejecutando…' : 'Ejecutar ahora'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ════════════════════════════ TAREAS ════════════════════════════
function TasksTab({ isAdmin, onErr }) {
  const [tasks, setTasks] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([af('/tasks'), af('/scripts')])
      .then(([t, s]) => { setTasks(t); setScripts(s); })
      .catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); }, [load]);

  const runNow = async (t) => {
    try { const r = await af(`/tasks/${t.id}/run-now`, { method: 'POST' }); onErr(''); alert(`Encolado en ${r.total_targets} equipo(s).`); load(); }
    catch (e) { onErr(e.message); }
  };
  const del = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea "${t.name}"?`)) return;
    try { await af(`/tasks/${t.id}`, { method: 'DELETE' }); load(); } catch (e) { onErr(e.message); }
  };
  const toggle = async (t) => {
    try { await af(`/tasks/${t.id}`, { method: 'PUT', body: JSON.stringify({ active: !t.active }) }); load(); }
    catch (e) { onErr(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {isAdmin && <button style={btnPrimary} onClick={() => setEditing({})}><Plus size={15} /> Nueva tarea</button>}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <Empty spin /> : tasks.length === 0 ? <Empty icon={Clock} text="No hay tareas programadas" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
              {['Nombre', 'Script', 'Destino', 'Frecuencia', 'Próxima', 'Estado', ''].map(h =>
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 12 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {tasks.map((t, i) => (
                <tr key={t.id} style={{ borderBottom: i < tasks.length - 1 ? '1px solid var(--bg)' : 'none' }}>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: 'var(--ink)' }}>{t.name}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{t.script_name || '—'}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{targetLabel(t)}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{schedLabel(t)}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{t.active ? fmtDate(t.next_run_at, true) : '—'}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge style={t.active ? { bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' } : { bg: 'var(--bg-subtle)', color: 'var(--ink-3)', border: 'var(--border)' }}>{t.active ? 'Activa' : 'Inactiva'}</Badge>
                  </td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {isAdmin && <>
                      <button title="Ejecutar ahora" style={{ ...iconBtn, color: 'var(--green)', marginRight: 6 }} onClick={() => runNow(t)}><Play size={14} /></button>
                      <button title={t.active ? 'Desactivar' : 'Activar'} style={{ ...iconBtn, marginRight: 6 }} onClick={() => toggle(t)}><Clock size={14} /></button>
                      <button title="Eliminar" style={{ ...iconBtn, color: 'var(--red)' }} onClick={() => del(t)}><Trash2 size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <TaskEditor scripts={scripts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onErr={onErr} />}
    </div>
  );
}

function TaskEditor({ scripts, onClose, onSaved, onErr }) {
  const { tags, scopes, assets } = useTargetData(onErr);
  const [f, setF] = useState({ name: '', script_id: '', schedule_kind: 'interval', interval_minutes: 60, daily_at: '08:00', active: true });
  const [target, setTarget] = useState({ type: 'scope', id: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name || !f.script_id) { onErr('Nombre y script son requeridos.'); return; }
    setSaving(true);
    const body = {
      ...f, target_type: target.type,
      target_scope_id: target.type === 'scope' ? target.id : null,
      target_tag_id: target.type === 'tag' ? target.id : null,
      target_asset_id: target.type === 'asset' ? target.id : null,
    };
    try { await af('/tasks', { method: 'POST', body: JSON.stringify(body) }); onSaved(); }
    catch (e) { onErr(e.message); setSaving(false); }
  };

  return (
    <Modal title="Nueva tarea programada" onClose={onClose}>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Nombre</label>
        <input style={input} value={f.name} onChange={e => set('name', e.target.value)} placeholder="Auditoría diaria de notebooks" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Script</label>
        <select style={input} value={f.script_id} onChange={e => set('script_id', e.target.value)}>
          <option value="">Elegir script…</option>
          {scripts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.language})</option>)}
        </select>
      </div>
      <TargetPicker target={target} setTarget={setTarget} tags={tags} scopes={scopes} assets={assets} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={label}>Frecuencia</label>
          <select style={input} value={f.schedule_kind} onChange={e => set('schedule_kind', e.target.value)}>
            <option value="interval">Cada N minutos</option>
            <option value="daily">Diaria a una hora</option>
          </select>
        </div>
        {f.schedule_kind === 'interval' ? (
          <div>
            <label style={label}>Intervalo (minutos)</label>
            <input style={input} type="number" min={1} value={f.interval_minutes} onChange={e => set('interval_minutes', parseInt(e.target.value) || 60)} />
          </div>
        ) : (
          <div>
            <label style={label}>Hora (24h)</label>
            <input style={input} type="time" value={f.daily_at} onChange={e => set('daily_at', e.target.value)} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Crear tarea'}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════ EJECUCIONES ════════════════════════════
function RunsTab({ onErr }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    af('/runs').then(setRuns).catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const finishedCount = runs.filter(r => (r.cmd_pending || 0) === 0).length;

  const clearFinished = async () => {
    if (!window.confirm(`¿Borrar ${finishedCount} ejecución(es) terminada(s)? Esto no afecta las que están en curso.`)) return;
    setClearing(true);
    try { const d = await af('/runs', { method: 'DELETE' }); await load(); onErr(''); if (d.deleted === 0) onErr('No hay ejecuciones terminadas para limpiar.'); }
    catch (e) { onErr(e.message); }
    finally { setClearing(false); }
  };

  const delOne = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Borrar esta ejecución del historial?')) return;
    try { await af(`/runs/${id}`, { method: 'DELETE' }); await load(); }
    catch (err) { onErr(err.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
        <button style={{ ...btnGhost, color: 'var(--red)', opacity: (finishedCount === 0 || clearing) ? 0.5 : 1 }}
          disabled={finishedCount === 0 || clearing} onClick={clearFinished}>
          <Trash2 size={14} /> {clearing ? 'Limpiando…' : `Limpiar terminadas${finishedCount ? ` (${finishedCount})` : ''}`}
        </button>
        <button style={btnGhost} onClick={load}><RefreshCw size={14} /> Actualizar</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <Empty spin /> : runs.length === 0 ? <Empty icon={Play} text="No hay ejecuciones registradas" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
              {['Script', 'Destino', 'Origen', 'Progreso', 'Fecha', ''].map(h =>
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 12 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={r.id} onClick={() => setDetail(r.id)}
                  style={{ borderBottom: i < runs.length - 1 ? '1px solid var(--bg)' : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: 'var(--ink)' }}>{r.script_name}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{r.target_desc}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge style={r.trigger === 'schedule' ? { bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' } : { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' }}>{r.trigger === 'schedule' ? 'Tarea' : 'Manual'}</Badge>
                  </td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>{progress(r)}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at, true)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(r.cmd_pending || 0) === 0 && (
                      <button title="Borrar del historial" onClick={e => delOne(e, r.id)}
                        style={{ ...iconBtn, color: 'var(--red)', marginRight: 4 }}><Trash2 size={14} /></button>
                    )}
                    <ChevronRight size={16} style={{ color: 'var(--ink-3)' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {detail && <RunDetail runId={detail} onClose={() => setDetail(null)} onErr={onErr} />}
    </div>
  );
}

function RunDetail({ runId, onClose, onErr }) {
  const [run, setRun] = useState(null);
  const [open, setOpen] = useState({});
  const load = useCallback(() => { af(`/runs/${runId}`).then(setRun).catch(e => onErr(e.message)); }, [runId, onErr]);
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  return (
    <Modal title={run ? `Ejecución — ${run.script_name}` : 'Ejecución'} onClose={onClose} width={760}>
      {!run ? <Empty spin /> : (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 14 }}>{run.target_desc} · {fmtDate(run.created_at, true)}</div>
          {run.commands.map(c => {
            const st = cmdStatus(c.status);
            const isOpen = open[c.id];
            return (
              <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                <div onClick={() => setOpen(p => ({ ...p, [c.id]: !p[c.id] }))}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer', background: 'var(--bg-subtle)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <st.icon size={15} style={{ color: st.color }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.hostname}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.os_name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Badge style={st.badge}>{st.label}{c.exit_code !== null && c.status !== 'pending' && c.status !== 'running' ? ` · exit ${c.exit_code}` : ''}</Badge>
                    <ChevronRight size={15} style={{ color: 'var(--ink-3)', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                  </div>
                </div>
                {isOpen && (
                  <pre style={{ margin: 0, padding: 14, background: '#0B1020', color: 'var(--border)', fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: 300 }}>
                    {c.output || (c.status === 'pending' || c.status === 'running' ? '(esperando al agente…)' : '(sin salida)')}
                  </pre>
                )}
              </div>
            );
          })}
        </>
      )}
    </Modal>
  );
}

// ════════════════════════════ VARIABLES ════════════════════════════
function VarsTab({ isAdmin, onErr }) {
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    af('/variables').then(setVars).catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); }, [load]);

  const del = async (v) => {
    if (!window.confirm(`¿Eliminar la variable "${v.key}"?`)) return;
    try { await af(`/variables/${v.id}`, { method: 'DELETE' }); load(); } catch (e) { onErr(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-2)' }}>Usalas en cualquier script con <code>{'{{KEY}}'}</code>. Los secretos se guardan cifrados (AES-256-GCM) y se resuelven al vuelo al ejecutar.</p>
        {isAdmin && <button style={btnPrimary} onClick={() => setEditing({})}><Plus size={15} /> Nueva variable</button>}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <Empty spin /> : vars.length === 0 ? <Empty icon={KeyRound} text="No hay variables globales" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
              {['Key', 'Valor', 'Descripción', 'Actualizado', ''].map(h =>
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 12 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {vars.map((v, i) => (
                <tr key={v.id} style={{ borderBottom: i < vars.length - 1 ? '1px solid var(--bg)' : 'none' }}>
                  <td style={{ padding: '12px 14px', fontFamily: 'ui-monospace, monospace', fontWeight: 600, color: 'var(--ink)' }}>{'{{'}{v.key}{'}}'}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>
                    {v.is_secret
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ink-2)' }}><Lock size={12} /> Secreto {v.has_value ? '' : '(vacío)'}</span>
                      : <span style={{ fontFamily: 'ui-monospace, monospace' }}>{v.value || '—'}</span>}
                  </td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)' }}>{v.description || '—'}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmtDate(v.updated_at)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isAdmin && <>
                      <button title="Editar" style={{ ...iconBtn, marginRight: 6 }} onClick={() => setEditing(v)}><Edit2 size={14} /></button>
                      <button title="Eliminar" style={{ ...iconBtn, color: 'var(--red)' }} onClick={() => del(v)}><Trash2 size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <VarEditor v={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onErr={onErr} />}
    </div>
  );
}

function VarEditor({ v, onClose, onSaved, onErr }) {
  const isNew = !v.id;
  const [f, setF] = useState({ key: v.key || '', value: '', is_secret: v.is_secret || false, description: v.description || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setF(p => ({ ...p, [k]: val }));

  const save = async () => {
    if (isNew && !/^[A-Za-z0-9_]+$/.test(f.key)) { onErr('Key inválida (solo A-Z, 0-9, _).'); return; }
    setSaving(true);
    try {
      if (isNew) await af('/variables', { method: 'POST', body: JSON.stringify(f) });
      else await af(`/variables/${v.id}`, { method: 'PUT', body: JSON.stringify({ value: f.value || undefined, is_secret: f.is_secret, description: f.description }) });
      onSaved();
    } catch (e) { onErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Nueva variable global' : `Editar ${v.key}`} onClose={onClose} width={480}>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Key</label>
        <input style={{ ...input, fontFamily: 'ui-monospace, monospace', ...(isNew ? {} : { background: 'var(--bg-subtle)', color: 'var(--ink-3)' }) }}
          value={f.key} disabled={!isNew} onChange={e => set('key', e.target.value.toUpperCase())} placeholder="GRAPH_CLIENT_SECRET" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Valor {!isNew && '(dejar vacío para no cambiar)'}</label>
        <input style={input} type={f.is_secret ? 'password' : 'text'} value={f.value} onChange={e => set('value', e.target.value)} placeholder={f.is_secret ? '••••••••' : ''} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={f.is_secret} onChange={e => set('is_secret', e.target.checked)} />
        Es un secreto (cifrar en reposo, ocultar en la UI)
      </label>
      <div style={{ marginBottom: 16 }}>
        <label style={label}>Descripción (opcional)</label>
        <input style={input} value={f.description} onChange={e => set('description', e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════ TAGS & SCOPES ════════════════════════════
function TagsTab({ isAdmin, onErr }) {
  const [tags, setTags] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState(false);
  const [editScope, setEditScope] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([af('/tags'), af('/scopes')])
      .then(([t, s]) => { setTags(t); setScopes(s); })
      .catch(e => onErr(e.message)).finally(() => setLoading(false));
  }, [onErr]);
  useEffect(() => { load(); }, [load]);

  const delTag = async (t) => {
    if (!window.confirm(`¿Eliminar el tag "${t.name}"? Se quita de todos los equipos y scopes.`)) return;
    try { await af(`/tags/${t.id}`, { method: 'DELETE' }); load(); } catch (e) { onErr(e.message); }
  };
  const delScope = async (s) => {
    if (!window.confirm(`¿Eliminar el scope "${s.name}"?`)) return;
    try { await af(`/scopes/${s.id}`, { method: 'DELETE' }); load(); } catch (e) { onErr(e.message); }
  };

  if (loading) return <div className="card"><Empty spin /></div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Tags */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Tags</h3>
          {isAdmin && <button style={btnGhost} onClick={() => setNewTag(true)}><Plus size={14} /> Tag</button>}
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {tags.length === 0 ? <Empty icon={TagIcon} text="Sin tags" /> : tags.map((t, i) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: i < tags.length - 1 ? '1px solid var(--bg)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: TAG_COLORS[t.color] || TAG_COLORS.slate }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· {t.asset_count} equipo(s)</span>
              </div>
              {isAdmin && <button title="Eliminar" style={{ ...iconBtn, width: 28, height: 28, color: 'var(--red)' }} onClick={() => delTag(t)}><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>Los scripts pueden crear/asignar tags emitiendo <code>##ITIL-SET-TAGS: Notebook</code> en su salida.</p>
      </div>

      {/* Scopes */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Scopes</h3>
          {isAdmin && <button style={btnGhost} onClick={() => setEditScope({})}><Plus size={14} /> Scope</button>}
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {scopes.length === 0 ? <Empty icon={Layers} text="Sin scopes" /> : scopes.map((s, i) => (
            <div key={s.id} style={{ padding: '11px 14px', borderBottom: i < scopes.length - 1 ? '1px solid var(--bg)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.asset_count} equipo(s)</span>
                  {isAdmin && <>
                    <button title="Editar" style={{ ...iconBtn, width: 28, height: 28 }} onClick={() => setEditScope(s)}><Edit2 size={13} /></button>
                    <button title="Eliminar" style={{ ...iconBtn, width: 28, height: 28, color: 'var(--red)' }} onClick={() => delScope(s)}><Trash2 size={13} /></button>
                  </>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.match_mode === 'all' ? 'Todos los tags:' : 'Cualquier tag:'}</span>
                {(s.tags || []).map(t => (
                  <span key={t.id} style={{ fontSize: 11, fontWeight: 600, color: TAG_COLORS[t.color] || 'var(--ink-2)' }}>{t.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {newTag && <TagEditor onClose={() => setNewTag(false)} onSaved={() => { setNewTag(false); load(); }} onErr={onErr} />}
      {editScope && <ScopeEditor scope={editScope} tags={tags} onClose={() => setEditScope(null)} onSaved={() => { setEditScope(null); load(); }} onErr={onErr} />}
    </div>
  );
}

function TagEditor({ onClose, onSaved, onErr }) {
  const [f, setF] = useState({ name: '', color: 'slate', description: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name) { onErr('Nombre requerido.'); return; }
    setSaving(true);
    try { await af('/tags', { method: 'POST', body: JSON.stringify(f) }); onSaved(); }
    catch (e) { onErr(e.message); setSaving(false); }
  };
  return (
    <Modal title="Nuevo tag" onClose={onClose} width={420}>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Nombre</label>
        <input style={input} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Notebook" />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={label}>Color</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(TAG_COLORS).map(([k, hex]) => (
            <button key={k} onClick={() => setF({ ...f, color: k })}
              style={{ width: 26, height: 26, borderRadius: '50%', background: hex, border: f.color === k ? '3px solid var(--ink)' : '2px solid var(--border)', cursor: 'pointer' }} />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Crear'}</button>
      </div>
    </Modal>
  );
}

function ScopeEditor({ scope, tags, onClose, onSaved, onErr }) {
  const isNew = !scope.id;
  const [f, setF] = useState({
    name: scope.name || '', description: scope.description || '',
    match_mode: scope.match_mode || 'any',
    tag_ids: (scope.tags || []).map(t => t.id),
  });
  const [saving, setSaving] = useState(false);
  const toggleTag = (id) => setF(p => ({ ...p, tag_ids: p.tag_ids.includes(id) ? p.tag_ids.filter(x => x !== id) : [...p.tag_ids, id] }));

  const save = async () => {
    if (!f.name) { onErr('Nombre requerido.'); return; }
    setSaving(true);
    try {
      if (isNew) await af('/scopes', { method: 'POST', body: JSON.stringify(f) });
      else await af(`/scopes/${scope.id}`, { method: 'PUT', body: JSON.stringify(f) });
      onSaved();
    } catch (e) { onErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Nuevo scope' : `Editar ${scope.name}`} onClose={onClose} width={480}>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Nombre</label>
        <input style={input} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Notebooks" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Coincidencia</label>
        <select style={input} value={f.match_mode} onChange={e => setF({ ...f, match_mode: e.target.value })}>
          <option value="any">Equipos con cualquiera de los tags</option>
          <option value="all">Equipos con todos los tags</option>
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={label}>Tags del scope</label>
        {tags.length === 0 ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Creá tags primero.</div> : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tags.map(t => (
              <button key={t.id} onClick={() => toggleTag(t.id)}
                style={{ fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
                  border: f.tag_ids.includes(t.id) ? '1px solid var(--ink)' : '1px solid var(--border)',
                  background: f.tag_ids.includes(t.id) ? 'var(--ink)' : '#fff',
                  color: f.tag_ids.includes(t.id) ? '#fff' : 'var(--ink-2)' }}>{t.name}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════ helpers UI ════════════════════════════
function Empty({ spin, icon: Icon, text }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
      {spin ? <RefreshCw size={20} style={{ animation: 'spin .8s linear infinite' }} />
        : <>{Icon && <Icon size={30} style={{ marginBottom: 8, opacity: 0.3 }} />}<div>{text}</div></>}
    </div>
  );
}

function progress(r) {
  const total = r.cmd_total || 0;
  const done = r.cmd_done || 0, failed = r.cmd_failed || 0, pending = r.cmd_pending || 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      {done > 0 && <span style={{ color: 'var(--green)', fontWeight: 600 }}>{done} OK</span>}
      {failed > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>{failed} error</span>}
      {pending > 0 && <span style={{ color: 'var(--amber)', fontWeight: 600 }}>{pending} pend.</span>}
      <span style={{ color: 'var(--ink-3)' }}>/ {total}</span>
    </div>
  );
}

function cmdStatus(s) {
  if (s === 'completed') return { icon: CheckCircle, color: 'var(--green)', label: 'OK', badge: { bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' } };
  if (s === 'failed' || s === 'timeout') return { icon: XCircle, color: 'var(--red)', label: s === 'timeout' ? 'Timeout' : 'Error', badge: { bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' } };
  if (s === 'running') return { icon: Loader, color: 'var(--accent)', label: 'Ejecutando', badge: { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' } };
  if (s === 'cancelled') return { icon: XCircle, color: 'var(--ink-3)', label: 'Cancelado', badge: { bg: 'var(--bg-subtle)', color: 'var(--ink-3)', border: 'var(--border)' } };
  return { icon: Clock, color: 'var(--amber)', label: 'Pendiente', badge: { bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)' } };
}

function targetLabel(t) {
  if (t.target_type === 'scope') return `Scope: ${t.scope_name || '—'}`;
  if (t.target_type === 'tag') return `Tag: ${t.tag_name || '—'}`;
  if (t.target_type === 'asset') return `Equipo: ${t.asset_hostname || '—'}`;
  return 'Todos los equipos';
}
function schedLabel(t) {
  if (t.schedule_kind === 'daily') return `Diaria ${t.daily_at ? String(t.daily_at).slice(0, 5) : ''}`;
  return `Cada ${t.interval_minutes || 60} min`;
}
function fmtDate(dt, withTime) {
  if (!dt) return '—';
  const d = new Date(dt);
  return withTime
    ? d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ════════════════════════════ CONSULTAS ════════════════════════════
// Flujo: filtrar el inventario → etiquetar el resultado en lote → disparar un script.
const QF = [
  { key: 'os_name', label: 'Sistema operativo', type: 'text' },
  { key: 'hostname', label: 'Hostname', type: 'text' },
  { key: 'software', label: 'Software instalado', type: 'software' },
  { key: 'software_usuario', label: 'Software por usuario', type: 'software' },
  { key: 'security_score', label: 'Security score', type: 'num' },
  { key: 'ram_gb', label: 'Memoria RAM (GB)', type: 'num' },
  { key: 'disk_used_pct', label: 'Disco usado (%)', type: 'num' },
  { key: 'pending_updates_count', label: 'Parches pendientes (n°)', type: 'num' },
  { key: 'patches_up_to_date', label: 'Parches al día', type: 'bool' },
  { key: 'agent_version', label: 'Versión de agente', type: 'text' },
  { key: 'criticidad', label: 'Criticidad', type: 'text' },
  { key: 'antivirus_name', label: 'Antivirus', type: 'text' },
  { key: 'antivirus_enabled', label: 'Antivirus activo', type: 'bool' },
  { key: 'quick_scan_age_days', label: 'Antivirus: días sin escaneo rápido', type: 'num' },
  { key: 'full_scan_age_days', label: 'Antivirus: días sin escaneo completo', type: 'num' },
  { key: 'bitlocker_enabled', label: 'BitLocker activo', type: 'bool' },
  { key: 'firewall_enabled', label: 'Firewall activo', type: 'bool' },
  { key: 'tag', label: 'Tag', type: 'tag' },
];
const QOPS = {
  text: [['contiene', 'contiene'], ['no_contiene', 'no contiene'], ['es', 'es'], ['no_es', 'no es'], ['vacio', 'está vacío'], ['no_vacio', 'no está vacío']],
  num: [['es', '='], ['no_es', '≠'], ['lt', '<'], ['lte', '≤'], ['gt', '>'], ['gte', '≥'], ['entre', 'entre'], ['vacio', 'está vacío'], ['no_vacio', 'no está vacío']],
  bool: [['es', 'es']],
  software: [['tiene', 'tiene'], ['no_tiene', 'no tiene']],
  tag: [['tiene', 'tiene'], ['no_tiene', 'no tiene']],
};
// Antigüedad de escaneo del antivirus: null = nunca corrió / sin dato (cuenta como atrasado).
const scanAgeLabel = (d) => (d == null ? 'nunca' : `${d} d`);
const scanAgeColor = (d, max) => (d == null || d > max ? 'var(--red)' : 'var(--ink)');
const qType = (field) => (QF.find(q => q.key === field) || {}).type || 'text';
const defOp = (field) => QOPS[qType(field)][0][0];

function ConsultasTab({ isAdmin, onErr }) {
  const [filters, setFilters] = useState([{ field: 'software', op: 'no_tiene', value: 'RustDesk' }]);
  const [match, setMatch] = useState('all');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [tagName, setTagName] = useState('');
  const [appliedTag, setAppliedTag] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [scriptId, setScriptId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [schedKind, setSchedKind] = useState('daily');
  const [schedInt, setSchedInt] = useState(60);
  const [dailyAt, setDailyAt] = useState('03:00');

  useEffect(() => { af('/scripts').then(setScripts).catch(() => {}); }, []);

  const setF = (i, patch) => setFilters(fs => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const onField = (i, field) => setF(i, { field, op: defOp(field), value: '', value2: '' });
  const addF = () => setFilters(fs => [...fs, { field: 'os_name', op: 'contiene', value: '' }]);
  const delF = (i) => setFilters(fs => fs.filter((_, j) => j !== i));

  const run = async () => {
    setLoading(true); setMsg(''); setAppliedTag(null); onErr('');
    try {
      const d = await af('/query', { method: 'POST', body: JSON.stringify({ filters, match }) });
      setRes(d); setSel(new Set(d.assets.map(a => a.id)));
    } catch (e) { onErr(e.message); setRes(null); }
    finally { setLoading(false); }
  };

  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = res && res.assets.length > 0 && sel.size === res.assets.length;
  const toggleAll = () => setSel(() => (allOn ? new Set() : new Set(res.assets.map(a => a.id))));

  const applyTag = async () => {
    if (!tagName.trim() || !sel.size) return;
    setBusy(true); setMsg(''); onErr('');
    try {
      const d = await af('/query/apply-tag', { method: 'POST', body: JSON.stringify({ asset_ids: [...sel], tag_name: tagName.trim() }) });
      setAppliedTag(d.tag); setMsg(`✓ Tag "${d.tag.name}" aplicado a ${d.tagged} equipos.`);
    } catch (e) { onErr(e.message); } finally { setBusy(false); }
  };

  const runScript = async () => {
    if (!scriptId || !sel.size) return;
    const sc = scripts.find(s => s.id === scriptId);
    if (!window.confirm(`¿Ejecutar "${sc ? sc.name : 'script'}" sobre ${sel.size} equipos?`)) return;
    setBusy(true); setMsg(''); onErr('');
    try {
      const d = await af('/run', { method: 'POST', body: JSON.stringify({ script_id: scriptId, target: { type: 'asset', ids: [...sel], name: appliedTag ? appliedTag.name : `consulta (${sel.size} eq.)` } }) });
      setMsg(`▷ Ejecución lanzada sobre ${d.total_targets} equipos. Seguila en la pestaña Ejecuciones.`);
    } catch (e) { onErr(e.message); } finally { setBusy(false); }
  };

  const createTask = async () => {
    if (!scriptId) { onErr('Elegí un script.'); return; }
    if (!appliedTag) { onErr('Aplicá un tag primero — la tarea programada apunta al tag (se recalcula sola).'); return; }
    const sc = scripts.find(s => s.id === scriptId);
    setBusy(true); setMsg(''); onErr('');
    try {
      const body = {
        name: `${sc ? sc.name : 'Script'} · ${appliedTag.name}`,
        script_id: scriptId, target_type: 'tag', target_tag_id: appliedTag.id,
        schedule_kind: schedKind,
        interval_minutes: schedKind === 'interval' ? Number(schedInt) || 60 : 60,
        daily_at: schedKind === 'daily' ? dailyAt : null, active: true,
      };
      await af('/tasks', { method: 'POST', body: JSON.stringify(body) });
      setMsg(`◷ Tarea programada "${body.name}" (${schedKind === 'daily' ? 'todos los días ' + dailyAt : 'cada ' + (Number(schedInt) || 60) + ' min'}). Vela en la pestaña Tareas.`);
    } catch (e) { onErr(e.message); } finally { setBusy(false); }
  };

  const sel2 = { height: 34, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5, background: 'var(--surface)', color: 'var(--ink)' };
  const cell = { padding: '7px 8px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };

  return (
    <div>
      {/* PASO 1 — Consulta */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--ink)', color: 'var(--surface)', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
          Consultá el inventario
        </div>

        {filters.map((f, i) => {
          const t = qType(f.field);
          const noVal = f.op === 'vacio' || f.op === 'no_vacio';
          return (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select style={{ ...sel2, minWidth: 190 }} value={f.field} onChange={e => onField(i, e.target.value)}>
                {QF.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}
              </select>
              <select style={{ ...sel2, minWidth: 120 }} value={f.op} onChange={e => setF(i, { op: e.target.value })}>
                {QOPS[t].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {!noVal && t === 'bool' && (
                <select style={{ ...sel2, minWidth: 90 }} value={f.value} onChange={e => setF(i, { value: e.target.value })}>
                  <option value="">—</option><option value="true">Sí</option><option value="false">No</option>
                </select>
              )}
              {!noVal && t !== 'bool' && f.op === 'entre' && (
                <>
                  <input style={{ ...sel2, width: 80 }} type="number" placeholder="mín" value={f.value || ''} onChange={e => setF(i, { value: e.target.value })} />
                  <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>y</span>
                  <input style={{ ...sel2, width: 80 }} type="number" placeholder="máx" value={f.value2 || ''} onChange={e => setF(i, { value2: e.target.value })} />
                </>
              )}
              {!noVal && t !== 'bool' && f.op !== 'entre' && (
                <input style={{ ...sel2, flex: 1, minWidth: 160 }} type={t === 'num' ? 'number' : 'text'} placeholder="valor…" value={f.value || ''} onChange={e => setF(i, { value: e.target.value })} />
              )}
              <button style={{ ...iconBtn, width: 30, height: 30 }} title="Quitar" onClick={() => delF(i)}><X size={14} /></button>
            </div>
          );
        })}

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
          <button style={btnGhost} onClick={addF}><Plus size={14} /> Agregar filtro</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
            Coincidir:
            <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
              {[['all', 'Todos (Y)'], ['any', 'Alguno (O)']].map(([v, l]) => (
                <span key={v} onClick={() => setMatch(v)} style={{ padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', background: match === v ? 'var(--ink)' : 'var(--surface)', color: match === v ? 'var(--surface)' : 'var(--ink-2)', fontWeight: match === v ? 600 : 400 }}>{l}</span>
              ))}
            </div>
          </div>
          <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={run} disabled={loading}>
            <Search size={14} /> {loading ? 'Consultando…' : 'Ejecutar consulta'}
          </button>
        </div>

        {res && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>
              <span style={{ color: 'var(--accent)' }}>{sel.size}</span>
              <span style={{ color: 'var(--ink-2)', fontWeight: 500, fontSize: 13 }}> seleccionados de {res.count} coincidencias</span>
            </div>
            {res.count === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '10px 0' }}>Sin equipos que coincidan con los filtros.</div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ position: 'sticky', top: 0, background: 'var(--bg-subtle)' }}>
                    <th style={{ ...cell, width: 30 }}><input type="checkbox" checked={allOn} onChange={toggleAll} /></th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Hostname</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>SO</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Score</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>RAM</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Disco</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }} title="Días desde el último escaneo rápido del antivirus">Quick</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }} title="Días desde el último escaneo completo del antivirus">Full</th>
                    <th style={{ ...cell, textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Tags</th>
                  </tr></thead>
                  <tbody>
                    {res.assets.map(a => (
                      <tr key={a.id}>
                        <td style={cell}><input type="checkbox" checked={sel.has(a.id)} onChange={() => toggle(a.id)} /></td>
                        <td style={{ ...cell, fontFamily: 'ui-monospace, monospace' }}>{a.hostname}{!a.tiene_agente && <span title="Sin agente" style={{ color: 'var(--ink-3)', marginLeft: 5 }}>·sin agente</span>}</td>
                        <td style={{ ...cell, color: 'var(--ink-2)' }}>{a.os_name || '—'}</td>
                        <td style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{a.security_score ?? '—'}</td>
                        <td style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{a.ram_gb ? Math.round(a.ram_gb) + ' GB' : '—'}</td>
                        <td style={{ ...cell, fontVariantNumeric: 'tabular-nums', color: a.disk_used_pct >= 85 ? 'var(--red)' : 'var(--ink)', fontWeight: a.disk_used_pct >= 85 ? 700 : 400 }}>{a.disk_used_pct != null ? a.disk_used_pct + '%' : '—'}</td>
                        <td style={{ ...cell, fontVariantNumeric: 'tabular-nums', color: scanAgeColor(a.quick_scan_age_days, 1) }}>{scanAgeLabel(a.quick_scan_age_days)}</td>
                        <td style={{ ...cell, fontVariantNumeric: 'tabular-nums', color: scanAgeColor(a.full_scan_age_days, 7) }}>{scanAgeLabel(a.full_scan_age_days)}</td>
                        <td style={cell}>{(a.tags || []).map(t => <span key={t} style={{ fontSize: 10.5, background: 'var(--bg-subtle)', color: 'var(--ink-2)', borderRadius: 4, padding: '1px 6px', marginRight: 3 }}>{t}</span>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PASO 2 y 3 — Etiquetar / Automatizar */}
      {res && res.count > 0 && isAdmin && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--ink)', color: 'var(--surface)', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Etiquetar el resultado</span>
            <input style={{ ...sel2, flex: 1, minWidth: 200 }} placeholder="nombre del tag (ej. sin-rustdesk)" value={tagName} onChange={e => setTagName(e.target.value)} />
            <button style={btnGhost} onClick={applyTag} disabled={busy || !tagName.trim() || !sel.size}><TagIcon size={14} /> Aplicar a los {sel.size}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <span style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--ink)', color: 'var(--surface)', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Automatizar</span>
            <select style={{ ...sel2, minWidth: 240 }} value={scriptId} onChange={e => setScriptId(e.target.value)}>
              <option value="">Elegí un script…</option>
              {scripts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>sobre {sel.size} equipos</span>
            <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={runScript} disabled={busy || !scriptId || !sel.size}><Play size={14} /> Ejecutar ahora</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 12.5, color: 'var(--ink-2)' }}>
            <span>o programar como tarea:</span>
            <select style={sel2} value={schedKind} onChange={e => setSchedKind(e.target.value)}>
              <option value="daily">Todos los días</option>
              <option value="interval">Cada N minutos</option>
            </select>
            {schedKind === 'daily'
              ? <input style={{ ...sel2, width: 110 }} type="time" value={dailyAt} onChange={e => setDailyAt(e.target.value)} />
              : <input style={{ ...sel2, width: 90 }} type="number" min="5" value={schedInt} onChange={e => setSchedInt(e.target.value)} />}
            <span style={{ color: 'var(--ink-3)', fontSize: 11.5 }}>{appliedTag ? `sobre tag "${appliedTag.name}"` : 'requiere aplicar un tag (paso 2)'}</span>
            <button style={{ ...btnGhost, marginLeft: 'auto' }} onClick={createTask} disabled={busy || !scriptId || !appliedTag}><Clock size={14} /> Crear tarea</button>
          </div>
          {msg && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--green)', background: 'var(--green-dim)', border: '1px solid #A7F3D0', borderRadius: 8, padding: '8px 12px' }}>{msg}</div>}
          <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-3)' }}>El script corre por el agente (run_script) sobre los equipos seleccionados; queda registrado en Ejecuciones. Tip: guardá la consulta como <b>Scope</b> desde Tags &amp; Scopes para reutilizarla y programarla.</div>
        </div>
      )}
    </div>
  );
}
